import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { PROVIDER_REASONING_LEVELS, reasoningLabel } from "../../lib/reasoning";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ExpressionEditor } from "../common/LazyExpressionEditor";
import { ExecModal } from "../shared/ExecModal";
import type { ExecRow } from "../shared/ExecModal";
import { SavedNote } from "../shared/Icons";
import { toastError } from "../shared/toast";

// Busca na internet (tool nativa kind="web"): config global + "buscar agora" +
// biblioteca de buscas. Só Dev (dev_tools). Gestores de IA só veem o toggle
// em Avançadas do agente — nada aqui é ensinado ou exposto pra eles.

interface Cfg {
  tool_description: string;
  query_description: string;
  prompt: string;
  provider: string;
  model: string;
  temperature: number;
  reasoning: string;
  max_results: number;
  fetch_pages: number;
  cache_ttl_hours: number;
}

interface AvailableModel {
  id: string;
  provider: string;
  model_name: string;
  label: string;
  thinking_levels: string[];
}

type Engine = "native" | "deepseek";

interface EngineRun {
  engine: Engine;
  answer: string;
  error?: string | null;
  cache_outcome: string;
  elapsed_ms: number;
  results: number;
  pages: number;
  tokens_in: number;
  tokens_out: number;
  sources: string[];
  unresponsive_engines: string[];
  library_error?: string | null;
  // Log completo da execução (web_tool): busca crua, seleção, modelo, páginas, pós.
  trace: Record<string, any>;
}

interface RunOut {
  runs: EngineRun[];
}

interface Item {
  id: string;
  query: string;
  engine: Engine;
  state: "pinned" | "active" | "expired";
  pinned: boolean;
  hits: number;
  last_agent_id: string | null;
  last_agent_name: string | null;
  last_hit_at: string | null;
  refreshed_at: string | null;
  expires_at: string | null;
  created_at: string;
}

interface CallOut {
  id: string;
  agent_id: string | null;
  agent_name: string | null;
  execution_id: string | null;
  outcome: string;
  elapsed_ms: number;
  engine: Engine;
  trace: Record<string, any> | null;
  created_at: string;
}

interface Detail extends Item {
  answer: string | null;
  results: { title: string; url: string; content: string }[];
  calls: CallOut[];
  agents: { agent_id: string | null; agent_name: string | null; calls: number }[];
}

const PROVIDERS = ["openai", "anthropic", "google", "grok", "openrouter", "deepseek", "meta"];
const PROVIDER_LABEL: Record<string, string> = {
  openai: "OpenAI", anthropic: "Anthropic", google: "Google", grok: "Grok", openrouter: "OpenRouter", deepseek: "DeepSeek", meta: "Meta",
};

const OUTCOME_LABEL: Record<string, string> = {
  hit: "Cache", pinned: "Cache fixo", miss: "Busca nova", expired: "Expirada → busca nova", fetched: "Busca + páginas",
};
const ENGINE_LABEL: Record<Engine, string> = { native: "Nativo", deepseek: "DeepSeek" };
const STATE_LABEL: Record<Item["state"], string> = { pinned: "Fixa", active: "Ativa", expired: "Expirada" };
const STATE_COLOR: Record<Item["state"], string> = { pinned: "var(--brand)", active: "var(--ink-mid)", expired: "var(--ink-muted)" };

const inp: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
const lbl: React.CSSProperties = { fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 };
const btnPrimary: React.CSSProperties = { padding: "8px 20px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" };
const btnGhost: React.CSSProperties = { padding: "6px 12px", background: "transparent", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 12, cursor: "pointer" };
const preBox: React.CSSProperties = { margin: 0, fontSize: 12, color: "var(--ink-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5, background: "var(--surface-raised)", borderRadius: 8, padding: 10 };
const meta: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)", display: "flex", flexWrap: "wrap", gap: 10 };
const cell: React.CSSProperties = { padding: "6px 8px", borderBottom: "1px solid var(--line-soft)" };

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString("pt-BR") : "—");

const badge: React.CSSProperties = { fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 6, border: "1px solid var(--line-strong)", color: "var(--ink-mid)", whiteSpace: "nowrap" };
const EngineBadge = ({ engine }: { engine: Engine }) => <span style={badge}>{ENGINE_LABEL[engine] ?? engine}</span>;

// Trace da execução, por etapa, cada uma num <details>. JSON cru no fim pra não
// esconder nada que a etapa não tenha resumido.
function TraceView({ trace }: { trace: Record<string, any> }) {
  const json = (v: unknown) => <pre style={{ ...preBox, fontSize: 11, maxHeight: 360, overflow: "auto" }}>{JSON.stringify(v, null, 2)}</pre>;
  const sec = (title: string, body: React.ReactNode, open = false) => (
    <details open={open} style={{ fontSize: 12 }}>
      <summary style={{ cursor: "pointer", color: "var(--ink)", fontWeight: 600 }}>{title}</summary>
      <div style={{ marginTop: 6 }}>{body}</div>
    </details>
  );
  const s = trace.search ?? {};
  const llm: any[] = trace.llm ?? [];
  const pages: any[] = trace.pages ?? [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {sec("Config e biblioteca", json({ config: trace.config, library: trace.library, bypass_cache: trace.bypass_cache }))}
      {sec(`Busca · ${s.status ?? "—"} · ${s.elapsed_ms != null ? `${s.elapsed_ms} ms` : "—"}${s.error ? " · ERRO" : ""}`, (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {s.error && <div style={{ color: "var(--danger, #b00020)" }}>{s.error}</div>}
          <div style={lbl}>Requisição</div>{json(s.request)}
          <div style={lbl}>Resposta crua</div>{json(s.response_raw)}
        </div>
      ))}
      {sec(`Seleção · ${(trace.selected ?? []).length} usados · ${(trace.dropped ?? []).length} descartados`, (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={lbl}>Usados</div>{json(trace.selected)}
          {(trace.dropped ?? []).length > 0 && <><div style={lbl}>Descartados</div>{json(trace.dropped)}</>}
          <div style={lbl}>Material enviado ao modelo</div><pre style={preBox}>{trace.material ?? ""}</pre>
        </div>
      ))}
      {llm.map((l, i) => sec(`Modelo ${i + 1} · ${l.provider}/${l.model} · ${l.elapsed_ms ?? "—"} ms · ${l.usage ? `${l.usage.prompt} in / ${l.usage.completion} out` : "sem uso"}${l.error ? " · ERRO" : ""}`, (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {l.error && <div style={{ color: "var(--danger, #b00020)" }}>{l.error}</div>}
          <div style={lbl}>System</div><pre style={preBox}>{l.system}</pre>
          <div style={lbl}>User</div><pre style={preBox}>{l.user}</pre>
          <div style={lbl}>Resposta crua</div><pre style={preBox}>{l.raw_answer ?? ""}</pre>
        </div>
      )))}
      {trace.llm_skipped && sec("Modelo", <div>Pulado: {trace.llm_skipped}</div>)}
      {pages.length > 0 && sec(`Páginas abertas · ${pages.filter((p) => p.chars > 0).length}/${pages.length} com texto${(trace.open_requested ?? []).length > 0 ? ` · modelo pediu ${trace.open_requested.join(", ")}` : " · K primeiras"}`, json(pages))}
      {trace.post && sec(`Pós-processo · ${(trace.post.links_removed ?? []).length} link(s) removido(s)${trace.post.not_found_marker ? " · NAO_ENCONTREI" : ""}`, json(trace.post))}
      {sec("Resultado final", json(trace.final))}
      {sec("JSON completo", json(trace))}
    </div>
  );
}

function RunCard({ r }: { r: EngineRun }) {
  return (
    <div style={{ flex: 1, minWidth: 280, border: "1px solid var(--line-soft)", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{ENGINE_LABEL[r.engine] ?? r.engine}</span>
        <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{(r.elapsed_ms / 1000).toFixed(1)}s</span>
      </div>
      <div style={meta}>
        <span>{OUTCOME_LABEL[r.cache_outcome] ?? r.cache_outcome}</span>
        <span>{r.results} resultados</span>
        {r.pages > 0 && <span>{r.pages} páginas abertas</span>}
        {(r.tokens_in > 0 || r.tokens_out > 0) && <span>{r.tokens_in.toLocaleString()} in / {r.tokens_out.toLocaleString()} out tk</span>}
        {r.unresponsive_engines.length > 0 && <span>Motores sem resposta: {r.unresponsive_engines.join(", ")}</span>}
        {r.library_error && <span>Biblioteca: {r.library_error}</span>}
      </div>
      <pre style={preBox}>{r.answer}</pre>
      {r.sources.length > 0 && (
        <div style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 2 }}>
          {r.sources.map((u) => <a key={u} href={u} target="_blank" rel="noreferrer" style={{ color: "var(--brand)", wordBreak: "break-all" }}>{u}</a>)}
        </div>
      )}
      {Object.keys(r.trace ?? {}).length > 0 && (
        <details style={{ fontSize: 12 }}>
          <summary style={{ cursor: "pointer", color: "var(--ink)", fontWeight: 600 }}>Trace completo</summary>
          <div style={{ marginTop: 8 }}><TraceView trace={r.trace} /></div>
        </details>
      )}
    </div>
  );
}

export function WebSearchTab() {
  const [sub, setSub] = useState<"config" | "library">("config");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--line-soft)" }}>
        {([["config", "Configuração"], ["library", "Biblioteca"]] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSub(id)}
            style={{
              padding: "8px 14px", background: "transparent", border: "none", cursor: "pointer", fontSize: 13,
              color: sub === id ? "var(--brand)" : "var(--ink-secondary)",
              borderBottom: sub === id ? "2px solid var(--brand)" : "2px solid transparent",
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {sub === "config" ? <ConfigSection /> : <LibrarySection />}
    </div>
  );
}

// ── Configuração + buscar agora ──────────────────────────────────────────────

function ConfigSection() {
  const qc = useQueryClient();
  const { data: config, isLoading } = useQuery<Cfg>({
    queryKey: ["dev-websearch-config"],
    queryFn: () => api.get("/dev/websearch/config"),
  });
  const { data: models = [] } = useQuery<AvailableModel[]>({
    queryKey: ["dev-available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });

  const [toolDesc, setToolDesc] = useState("");
  const [queryDesc, setQueryDesc] = useState("");
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("google");
  const [model, setModel] = useState("");
  const [temperature, setTemperature] = useState("0.2");
  const [reasoning, setReasoning] = useState("off");
  const [maxResults, setMaxResults] = useState("8");
  const [fetchPages, setFetchPages] = useState("0");
  const [ttlHours, setTtlHours] = useState("24");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!config || loaded) return;
    setToolDesc(config.tool_description ?? "");
    setQueryDesc(config.query_description ?? "");
    setPrompt(config.prompt ?? "");
    setProvider(config.provider || "google");
    setModel(config.model ?? "");
    setTemperature(String(config.temperature ?? 0.2));
    setReasoning(config.reasoning ?? "off");
    setMaxResults(String(config.max_results ?? 8));
    setFetchPages(String(config.fetch_pages ?? 0));
    setTtlHours(String(config.cache_ttl_hours ?? 24));
    setLoaded(true);
  }, [config, loaded]);

  const providerModels = models.filter((m) => m.provider === provider);
  const levels = PROVIDER_REASONING_LEVELS[provider] ?? [];
  const reasoningOptions = levels.includes("off") ? levels : ["off", ...levels];

  const mut = useMutation({
    mutationFn: () => {
      const body: Partial<Cfg> = {};
      if (config) {
        if (toolDesc !== config.tool_description) body.tool_description = toolDesc;
        if (queryDesc !== config.query_description) body.query_description = queryDesc;
        if (prompt !== config.prompt) body.prompt = prompt;
        if (provider !== config.provider) body.provider = provider;
        if (model !== config.model) body.model = model;
        const temp = parseFloat(temperature.replace(",", "."));
        if (Number.isFinite(temp) && temp !== config.temperature) body.temperature = temp;
        if (reasoning !== config.reasoning) body.reasoning = reasoning;
        const mr = parseInt(maxResults); if (Number.isFinite(mr) && mr !== config.max_results) body.max_results = mr;
        const fp = parseInt(fetchPages); if (Number.isFinite(fp) && fp !== config.fetch_pages) body.fetch_pages = fp;
        const ttl = parseInt(ttlHours); if (Number.isFinite(ttl) && ttl !== config.cache_ttl_hours) body.cache_ttl_hours = ttl;
      }
      return api.patch("/dev/websearch/config", body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dev-websearch-config"] }),
    onError: (e: any) => toastError(e?.payload?.detail ?? "Falha ao salvar a configuração da busca."),
  });

  function changeProvider(p: string) {
    setProvider(p);
    const first = models.find((m) => m.provider === p);
    setModel(first?.model_name ?? "");
    const lv = PROVIDER_REASONING_LEVELS[p] ?? [];
    if (!lv.includes(reasoning) && reasoning !== "off") setReasoning("off");
  }

  // Buscar agora: roda a tool de verdade e já grava na biblioteca (agente vazio).
  const [runQuery, setRunQuery] = useState("");
  const [runEngine, setRunEngine] = useState<Engine | "both">("native");
  const [bypassCache, setBypassCache] = useState(true);
  const [runOut, setRunOut] = useState<RunOut | null>(null);
  // Histórico da sessão (bateria de testes): cada rodada com query, hora e os motores
  // inteiros (resposta, métricas, trace). Baixa em .json pra análise fora do painel.
  type RunRecord = { query: string; engine: string; bypass_cache: boolean; ran_at: string; runs: EngineRun[] };
  const [history, setHistory] = useState<RunRecord[]>([]);
  // A query vai como variável da mutation: o histórico grava a que foi disparada, não
  // o que está na caixa quando a resposta chega (buscas em sequência trocavam o rótulo).
  const run = useMutation({
    mutationFn: (vars: { query: string; engine: Engine | "both"; bypass_cache: boolean }) => api.post<RunOut>("/dev/websearch/run", vars, 120_000),
    onSuccess: (out, vars) => {
      setRunOut(out);
      setHistory((h) => [...h, { ...vars, ran_at: new Date().toISOString(), runs: out.runs }]);
      qc.invalidateQueries({ queryKey: ["dev-websearch-searches"] });
    },
    onError: (e: any) => toastError(e?.payload?.detail ?? "Falha ao buscar."),
  });
  const slug = (s: string) => s.toLowerCase().normalize("NFD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  function download(name: string, data: unknown) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (isLoading) return <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando...</div>;

  return (
    <div style={{ maxWidth: 720, display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Busca na internet</span>
        <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 4 }}>
          Config global da tool nativa <code>buscar_na_internet</code>. Ela entra sozinha nos agentes com "Acesso à internet" ligado em Avançadas
          (vale ao publicar). Provedor e modelo são obrigatórios antes do primeiro uso.
        </div>
      </div>

      <div>
        <label style={lbl}>Descrição da tool (o que o agente principal lê pra decidir quando buscar)</label>
        <ExpressionEditor value={toolDesc} onChange={setToolDesc} autoGrow minHeight={90} maxHeight={300} />
      </div>
      <div>
        <label style={lbl}>Descrição do parâmetro "query" (como o agente deve escrever a busca)</label>
        <ExpressionEditor value={queryDesc} onChange={setQueryDesc} autoGrow minHeight={70} maxHeight={240} />
      </div>
      <div>
        <label style={lbl}>Prompt do agente secundário (avalia os resultados e escreve a resposta) — os três textos aceitam {"{{ variáveis }}"}; o que não resolve na hora fica em branco</label>
        <ExpressionEditor value={prompt} onChange={setPrompt} autoGrow minHeight={200} maxHeight={520} />
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Provedor</label>
          <select style={inp} value={provider} onChange={(e) => changeProvider(e.target.value)}>
            {PROVIDERS.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
          </select>
        </div>
        <div style={{ flex: 2 }}>
          <label style={lbl}>Modelo</label>
          {providerModels.length > 0 ? (
            <select style={inp} value={model} onChange={(e) => setModel(e.target.value)}>
              {!providerModels.some((m) => m.model_name === model) && model && <option value={model}>{model}</option>}
              {providerModels.map((m) => <option key={m.id} value={m.model_name}>{m.label}</option>)}
            </select>
          ) : (
            <input style={inp} value={model} onChange={(e) => setModel(e.target.value)} placeholder="ex: gemini-flash-lite" spellCheck={false} />
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Temperatura (0–2)</label>
          <input style={inp} type="number" min={0} max={2} step={0.1} value={temperature} onChange={(e) => setTemperature(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Raciocínio</label>
          <select style={inp} value={reasoning} onChange={(e) => setReasoning(e.target.value)}>
            {reasoningOptions.map((t) => <option key={t} value={t}>{reasoningLabel(t)}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Resultados no material (1–20)</label>
          <input style={inp} type="number" min={1} max={20} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Páginas a abrir se não achou (0–3)</label>
          <input style={inp} type="number" min={0} max={3} value={fetchPages} onChange={(e) => setFetchPages(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Validade do cache (horas)</label>
          <input style={inp} type="number" min={1} max={720} value={ttlHours} onChange={(e) => setTtlHours(e.target.value)} />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => mut.mutate()} disabled={mut.isPending || !model.trim()} style={btnPrimary}>
          {mut.isPending ? "Salvando..." : "Salvar"}
        </button>
        <SavedNote show={mut.isSuccess} />
      </div>

      <div style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Buscar agora</span>
        <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>
          Roda a tool de verdade com a config salva, mostra tempo, custo e o trace completo de cada motor, e já grava o resultado na biblioteca (o trace não é gravado).
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            style={{ ...inp, flex: 1, minWidth: 200 }}
            value={runQuery}
            onChange={(e) => setRunQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && runQuery.trim() && !run.isPending) run.mutate({ query: runQuery.trim(), engine: runEngine, bypass_cache: bypassCache }); }}
            placeholder="ex: tabela fipe onix lt 2022"
          />
          <select style={{ ...inp, width: "auto" }} value={runEngine} onChange={(e) => setRunEngine(e.target.value as Engine | "both")}>
            <option value="native">Nativo</option>
            <option value="deepseek">DeepSeek</option>
            <option value="both">Ambos</option>
          </select>
          <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={bypassCache} onChange={(e) => setBypassCache(e.target.checked)} style={{ accentColor: "var(--brand)" }} />
            Ignorar cache
          </label>
          <button onClick={() => run.mutate({ query: runQuery.trim(), engine: runEngine, bypass_cache: bypassCache })} disabled={run.isPending || !runQuery.trim()} style={{ ...btnPrimary, whiteSpace: "nowrap" }}>
            {run.isPending ? "Buscando..." : "Buscar"}
          </button>
        </div>
        {runOut && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            {runOut.runs.map((r) => <RunCard key={r.engine} r={r} />)}
          </div>
        )}
        {history.length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button style={btnGhost} onClick={() => { const last = history[history.length - 1]; download(`busca-${slug(last.query)}-${last.ran_at.slice(0, 19).replace(/[:T]/g, "")}.json`, last); }}>
              Baixar trace desta busca (.json)
            </button>
            <button style={btnGhost} onClick={() => download(`buscas-${new Date().toISOString().slice(0, 10)}-${history.length}.json`, { exported_at: new Date().toISOString(), searches: history })}>
              Baixar todas da sessão ({history.length})
            </button>
            <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>Cada busca leva query, hora e, por motor, resposta, métricas e o trace inteiro.</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Biblioteca ───────────────────────────────────────────────────────────────

function LibrarySection() {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"recent" | "hits" | "alpha">("recent");
  const [state, setState] = useState<"all" | "active" | "expired" | "pinned">("all");
  const [engine, setEngine] = useState<"all" | Engine>("all");
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);

  const params = new URLSearchParams({ q, sort, state, engine, page_size: "100" });
  const { data, isLoading } = useQuery<{ items: Item[]; total: number }>({
    queryKey: ["dev-websearch-searches", q, sort, state, engine],
    queryFn: () => api.get(`/dev/websearch/searches?${params.toString()}`),
  });
  const items = data?.items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...inp, maxWidth: 320 }} value={text} onChange={(e) => setText(e.target.value)} placeholder="Buscar por palavras (qualquer ordem)" />
        <select style={{ ...inp, width: "auto" }} value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="recent">Mais recentes</option>
          <option value="hits">Mais chamadas</option>
          <option value="alpha">Ordem alfabética</option>
        </select>
        <select style={{ ...inp, width: "auto" }} value={state} onChange={(e) => setState(e.target.value as typeof state)}>
          <option value="all">Todas</option>
          <option value="active">Ativas</option>
          <option value="expired">Expiradas</option>
          <option value="pinned">Fixas</option>
        </select>
        <select style={{ ...inp, width: "auto" }} value={engine} onChange={(e) => setEngine(e.target.value as typeof engine)}>
          <option value="all">Todos os motores</option>
          <option value="native">Nativo</option>
          <option value="deepseek">DeepSeek</option>
        </select>
        <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{data ? `${data.total} busca${data.total === 1 ? "" : "s"}` : ""}</span>
      </div>

      {isLoading ? (
        <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando...</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhuma busca ainda. Elas aparecem aqui conforme os agentes (ou o "Buscar agora") usam a tool.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ color: "var(--ink-secondary)", textAlign: "left" }}>
                {["Busca", "Motor", "Estado", "Chamadas", "Último agente", "Última chamada", "Expira"].map((h) => (
                  <th key={h} style={{ ...cell, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  onClick={() => setSelected(it.id === selected ? null : it.id)}
                  style={{ cursor: "pointer", background: it.id === selected ? "var(--surface-raised)" : "transparent" }}
                >
                  <td style={{ ...cell, color: "var(--ink)" }}>{it.query}</td>
                  <td style={cell}><EngineBadge engine={it.engine} /></td>
                  <td style={{ ...cell, color: STATE_COLOR[it.state], fontWeight: 600 }}>{STATE_LABEL[it.state]}</td>
                  <td style={{ ...cell, color: "var(--ink-mid)" }}>{it.hits}</td>
                  <td style={{ ...cell, color: "var(--ink-mid)" }}>{it.last_agent_name ?? "—"}</td>
                  <td style={{ ...cell, color: "var(--ink-muted)" }}>{fmtDate(it.last_hit_at)}</td>
                  <td style={{ ...cell, color: "var(--ink-muted)" }}>{it.pinned ? "nunca" : fmtDate(it.expires_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <SearchDetail
          id={selected}
          onDeleted={() => { setSelected(null); qc.invalidateQueries({ queryKey: ["dev-websearch-searches"] }); }}
        />
      )}
    </div>
  );
}

function SearchDetail({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const qc = useQueryClient();
  const { data: d, isLoading } = useQuery<Detail>({
    queryKey: ["dev-websearch-search", id],
    queryFn: () => api.get(`/dev/websearch/searches/${id}`),
  });
  const [answer, setAnswer] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [execOpen, setExecOpen] = useState<{ row: ExecRow; agentId: string } | null>(null);
  const [traceOpen, setTraceOpen] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["dev-websearch-search", id] });
    qc.invalidateQueries({ queryKey: ["dev-websearch-searches"] });
  };
  const patch = useMutation({
    mutationFn: (body: { pinned?: boolean; answer?: string }) => api.patch(`/dev/websearch/searches/${id}`, body),
    // Fixar/desafixar não pode descartar um rascunho de resposta em edição:
    // só sai do modo de edição quando o que foi salvo foi a resposta.
    onSuccess: (_data, variables) => { if (variables.answer !== undefined) setAnswer(null); refresh(); },
    onError: (e: any) => toastError(e?.payload?.detail ?? "Falha ao salvar."),
  });
  const del = useMutation({
    mutationFn: () => api.delete(`/dev/websearch/searches/${id}`),
    onSuccess: onDeleted,
    onError: (e: any) => toastError(e?.payload?.detail ?? "Falha ao apagar."),
  });

  async function openExecution(c: CallOut) {
    if (!c.agent_id || !c.execution_id) return;
    try {
      const row = await api.get<ExecRow>(`/agents/${c.agent_id}/executions/${c.execution_id}`);
      setExecOpen({ row, agentId: c.agent_id });
    } catch (e: any) {
      toastError(e?.payload?.detail ?? "Execução não encontrada.");
    }
  }

  if (execOpen) {
    return (
      <ExecModal
        exec={execOpen.row}
        stepsUrl={`/agents/${execOpen.agentId}/executions/${execOpen.row.id}/steps`}
        onClose={() => setExecOpen(null)}
      />
    );
  }
  if (isLoading || !d) return <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando...</div>;

  const editing = answer !== null;
  return (
    <div style={{ border: "1px solid var(--line-soft)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
      {confirmDelete && (
        <ConfirmDialog
          title="Apagar esta busca?"
          message="A resposta e o histórico de chamadas somem. A próxima vez que um agente buscar isso, a busca é refeita do zero."
          confirmLabel="Apagar"
          onConfirm={() => { setConfirmDelete(false); del.mutate(); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 }}>{d.query} <EngineBadge engine={d.engine} /></div>
          <div style={{ ...meta, marginTop: 4 }}>
            <span style={{ color: STATE_COLOR[d.state], fontWeight: 600 }}>{STATE_LABEL[d.state]}</span>
            <span>{d.hits} chamada{d.hits === 1 ? "" : "s"}</span>
            <span>Gerada em {fmtDate(d.refreshed_at)}</span>
            <span>{d.pinned ? "Nunca expira" : `Expira em ${fmtDate(d.expires_at)}`}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={btnGhost} disabled={patch.isPending} onClick={() => patch.mutate({ pinned: !d.pinned })}>
            {d.pinned ? "Desafixar" : "Fixar (cache permanente)"}
          </button>
          <button style={btnGhost} onClick={() => setAnswer(editing ? null : (d.answer ?? ""))}>{editing ? "Cancelar edição" : "Editar resposta"}</button>
          <button style={btnGhost} onClick={() => setConfirmDelete(true)}>Apagar</button>
        </div>
      </div>

      <div>
        <div style={{ ...lbl, marginBottom: 6 }}>Resposta entregue ao agente</div>
        {editing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} style={{ ...inp, height: 140, resize: "vertical", lineHeight: 1.5 }} />
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button style={btnPrimary} disabled={patch.isPending || !answer.trim()} onClick={() => patch.mutate({ answer })}>Salvar resposta</button>
              <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>Dica: fixe a busca depois de curar, senão a resposta é refeita quando expirar.</span>
            </div>
          </div>
        ) : (
          <pre style={preBox}>{d.answer ?? "—"}</pre>
        )}
      </div>

      {d.results.length > 0 && (
        <div>
          <div style={{ ...lbl, marginBottom: 6 }}>Resultados usados</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {d.results.map((r, i) => (
              <div key={`${r.url}-${i}`} style={{ fontSize: 12 }}>
                <a href={r.url} target="_blank" rel="noreferrer" style={{ color: "var(--brand)", wordBreak: "break-all" }}>{r.title || r.url}</a>
                {r.content && <div style={{ color: "var(--ink-muted)" }}>{r.content}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {d.agents.length > 0 && (
        <div style={meta}>
          <span>Por agente:</span>
          {d.agents.map((a, i) => <span key={`${a.agent_id ?? "painel"}-${i}`}>{a.agent_name ?? "Painel Dev"} · {a.calls}</span>)}
        </div>
      )}

      <div>
        <div style={{ ...lbl, marginBottom: 6 }}>Últimas chamadas</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ color: "var(--ink-secondary)", textAlign: "left" }}>
                {["Quando", "Agente", "Resultado", "Tempo", ""].map((h, i) => (
                  <th key={`${h}-${i}`} style={{ ...cell, padding: "4px 8px", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.calls.map((c) => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td style={{ ...cell, padding: "4px 8px", color: "var(--ink-muted)" }}>{fmtDate(c.created_at)}</td>
                    <td style={{ ...cell, padding: "4px 8px", color: "var(--ink-mid)" }}>{c.agent_name ?? "Painel Dev"}</td>
                    <td style={{ ...cell, padding: "4px 8px", color: "var(--ink-mid)" }}>{OUTCOME_LABEL[c.outcome] ?? c.outcome}</td>
                    <td style={{ ...cell, padding: "4px 8px", color: "var(--ink-muted)" }}>{(c.elapsed_ms / 1000).toFixed(1)}s</td>
                    <td style={{ ...cell, padding: "4px 8px", display: "flex", gap: 6 }}>
                      {c.agent_id && c.execution_id && (
                        <button style={{ ...btnGhost, padding: "3px 8px" }} onClick={() => openExecution(c)}>Ver execução</button>
                      )}
                      {c.trace && (
                        <button style={{ ...btnGhost, padding: "3px 8px" }} onClick={() => setTraceOpen(traceOpen === c.id ? null : c.id)}>
                          {traceOpen === c.id ? "Fechar trace" : "Trace"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {traceOpen === c.id && c.trace && (
                    <tr>
                      <td colSpan={5} style={{ ...cell, padding: "8px" }}><TraceView trace={c.trace} /></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
